import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarRange, Loader2, RotateCcw, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { api } from '../lib/api';
import { toast } from '../hooks/use-toast';

const SOURCE_STYLES = {
  admin: 'bg-[#ff2bd6] text-white',
  'fortnite.gg': 'bg-[#2ee6c1] text-[#064733]',
  auto: 'bg-[#ffd900] text-[#3a1064]',
  seed: 'bg-[#3a1064]/10 text-[#3a1064]',
};
const SOURCE_LABEL = { admin: 'ADMIN', 'fortnite.gg': 'AUTO · GG', auto: 'AUTO', seed: 'AUTO · LEGACY' };

const selectCls = 'h-9 rounded-lg border-2 border-[#3a1064] bg-[#fff7e0] px-2 text-sm font-black text-[#3a1064]';

function SeasonSelect({ value, options, onChange, testid, disabled }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testid} className={selectCls}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function FamilyRow({ f, options, busy, onSet, onReset }) {
  const slug = f.family.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return (
    <div className="flex flex-wrap items-center gap-3 py-2 border-b border-[#3a1064]/10 last:border-0" data-testid={`season-row-${slug}`}>
      <div className="flex-1 min-w-[140px] font-black text-[#3a1064]">{f.family}</div>
      <div className="text-xs font-bold text-[#3a1064]/60 w-20">{f.sprite_count} sprites</div>
      <span className={`text-[10px] font-black px-2 py-1 rounded-md tracking-wider ${SOURCE_STYLES[f.source] || SOURCE_STYLES.auto}`} data-testid={`season-source-${slug}`}>
        {SOURCE_LABEL[f.source] || 'AUTO'}
      </span>
      <SeasonSelect value={f.season} options={options} disabled={busy} testid={`season-select-${slug}`} onChange={(v) => onSet(f.family, v)} />
      <Button size="sm" variant="ghost" disabled={busy || f.source !== 'admin'} onClick={() => onReset(f.family)}
        data-testid={`season-reset-${slug}`} className="h-9 px-2 text-[#3a1064] font-black disabled:opacity-30">
        <RotateCcw className="h-4 w-4" strokeWidth={3} />
      </Button>
    </div>
  );
}

export function SeasonManagerCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    try { setData(await api.adminGetSeasons()); } catch (e) {
      toast({ title: 'Failed to load seasons', description: String(e.message || e), variant: 'destructive' });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    setBusy(true);
    try { setData(await fn()); toast({ title: msg }); } catch (e) {
      toast({ title: 'Save failed', description: String(e.message || e), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const options = useMemo(() => {
    if (!data) return [];
    const codes = new Set([...data.seasons.map((s) => s.value), data.next_season]);
    const key = (c) => { const m = /^C(\d+)S(\d+)$/.exec(c); return m ? Number(m[1]) * 1000 + Number(m[2]) : 0; };
    return Array.from(codes).sort((a, b) => key(b) - key(a));
  }, [data]);

  const families = useMemo(() => (data?.families || []).filter((f) => f.family.toLowerCase().includes(q.trim().toLowerCase())), [data, q]);

  if (!data) {
    return (
      <div className="rounded-2xl border-2 border-[#3a1064] bg-white p-6 shadow-[0_4px_0_#3a1064] flex items-center gap-3 text-[#3a1064] font-black">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading seasons...
      </div>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-[#3a1064] bg-white p-6 shadow-[0_4px_0_#3a1064]" data-testid="season-manager-card">
      <div className="flex flex-wrap items-start gap-4 mb-4">
        <div className="h-12 w-12 rounded-xl bg-[#2ee6c1] flex items-center justify-center shrink-0">
          <CalendarRange className="h-6 w-6 text-[#064733]" strokeWidth={3} />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-[10px] font-black uppercase tracking-[2px] text-[#3a1064]/70">Season manager</div>
          <div className="text-lg sm:text-xl font-black text-[#3a1064] leading-tight">Auto season detection</div>
          <div className="text-sm font-bold text-[#3a1064]/80 mt-1">
            New families from fortnite.gg land in the current season automatically. A new fortnite.gg season number rolls the site over on its own. Change a family below to override.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-black uppercase text-[#3a1064]/70">Current</span>
          <SeasonSelect value={data.current_season} options={options} disabled={busy} testid="current-season-select"
            onChange={(v) => run(() => api.adminSetCurrentSeason(v), `Current season set to ${v}`)} />
        </div>
      </div>

      <div className="relative mb-3">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#3a1064]/50" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search families..." data-testid="season-family-search"
          className="pl-9 h-10 border-2 border-[#3a1064]/40 font-bold text-[#3a1064]" />
      </div>

      <div className="max-h-[420px] overflow-y-auto pr-1" data-testid="season-family-list">
        {families.map((f) => (
          <FamilyRow key={f.family} f={f} options={options} busy={busy}
            onSet={(fam, s) => run(() => api.adminSetFamilySeason(fam, s), `${fam} → ${s}`)}
            onReset={(fam) => run(() => api.adminResetFamilySeason(fam), `${fam} reset to auto`)} />
        ))}
        {!families.length && <div className="text-sm font-bold text-[#3a1064]/60 py-4">No families match.</div>}
      </div>
    </div>
  );
}
